import React, { useState } from "react";
import { set, useForm } from "react-hook-form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";

import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowLeftIcon, Loader2, LoaderIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AuthDisplay from "./components/AuthDisplay";
import HelmetMetaData from "@/components/HelmetMetaData";
import { PostRequest } from "@/lib/http";
import { toast } from "sonner";

const ResetPassword = () => {
  const [previewType, setPreviewType] = useState("reset");
  const [userEmail, setUserEmail] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const router = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  // Handle email form submission
  const onSubmit = async (body) => {
    const { err } = await PostRequest({
      url: "/api/password-reset/init",
      body,
      setState: setIsSubmitting,
    });

    if (!err) {
      setUserEmail(body.email);
      setPreviewType("confirm");
      reset();
    } else {
      toast.error("Reset password error", {
        description: err,
      });
    }
  };

  // Handle OTP verification
  const handleOTPSubmit = async () => {
    const { err } = await PostRequest({
      url: "/api/password-reset/verify-code",
      body: {
        email: userEmail,
        code: otpValue,
      },
      setState: setIsOTPLoading,
    });

    if (!err) {
      localStorage.setItem("userEmail", userEmail);
      localStorage.setItem("code", otpValue);
      toast.success("Code verification", {
        description: "Verification successful",
      });

      router("/create-password");
    } else {
      toast.error("Code verification error", {
        description: err,
      });
    }
  };

  const handleOTPChange = (value) => {
    setOtpValue(value);
  };

  const handleBack = () => {
    if (previewType === "confirm") {
      setPreviewType("reset");
    } else if (previewType === "codeConfirm") {
      setPreviewType("confirm");
    } else {
      router("/login");
    }
  };

  return (
    <>
      {previewType === "reset" && (
        <HelmetMetaData
          title="Reset Password - Billzpay"
          description="Reset your Billzpay password to regain access to your account. Follow the instructions sent to your email to create a new password and continue enjoying seamless payments."
        />
      )}

      {previewType === "confirm" && (
        <HelmetMetaData
          title="Code Sent - Billzpay"
          description="A verification code has been sent to your registered email. Please check your inbox to proceed with resetting your password and regain access to your Billzpay account."
        />
      )}

      <section
        style={{
          background:
            "linear-gradient(135deg, #223867 0%, #33559b 50%, #111c34 100%)",
        }}
        className="h-dvh overflow-y-hidden"
      >
        <div className="flex h-full w-full items-center justify-center border border-white/10 lg:flex-row lg:justify-start">
          <div className="dark:bg-darkBg relative h-full w-full overflow-hidden rounded-none bg-white p-5 sm:w-[450px] md:h-[650px] lg:h-full lg:w-[40%] lg:px-10">
            <img
              src="/logo.svg"
              alt="ifebaik-logo"
              className="absolute top-3 right-5 w-16 lg:w-14"
            />
            {/* Back Icon */}
            <div
              onClick={handleBack}
              className="absolute top-6 left-5 cursor-pointer"
            >
              <ArrowLeftIcon className="text-2xl" />
            </div>

            <AnimatePresence mode="wait">
              {previewType === "reset" && (
                <motion.div
                  key="reset"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex h-full w-full items-center"
                >
                  <div className="w-full rounded-3xl py-10 xl:p-10 xl:shadow-2xl">
                    <h2 className="font-archivo mb-2 text-[2.4rem] leading-[1] font-semibold text-black/85 dark:text-white">
                      Reset your password
                    </h2>

                    <p className="leading-[1.3] text-gray-500">
                      Enter your email address and we will send you instructions
                      to reset your password.
                    </p>

                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className="mt-5 flex flex-col gap-3"
                    >
                      <div className="flex flex-col gap-1">
                        <label htmlFor="email" className="text-sm font-medium">
                          Email Address
                        </label>
                        <input
                          id="email"
                          type="text"
                          {...register("email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address",
                            },
                          })}
                          className="w-full rounded-md border border-slate-400 px-3 py-2.5"
                        />
                        {errors.email && (
                          <span className="text-xs font-medium text-red-500">
                            {errors.email.message}
                          </span>
                        )}
                      </div>

                      <div className="flex gap-3">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className={`btn w-full cursor-pointer rounded-md px-8 py-2.5 font-medium text-white transition-all duration-150 disabled:pointer-events-none disabled:cursor-not-allowed`}
                        >
                          {isSubmitting ? (
                            <div className="flex items-center justify-center gap-2">
                              <Loader2 className="animate-spin" />
                              <span>Validating email...</span>
                            </div>
                          ) : (
                            "Reset"
                          )}
                        </button>
                      </div>

                      <p className="text-center text-sm text-gray-500">
                        Remember your password?{" "}
                        <Link
                          to="/login"
                          className="font-medium text-black underline"
                        >
                          Login
                        </Link>
                      </p>
                    </form>

                    <div className="absolute bottom-4 left-1/2 mt-10 w-full -translate-x-1/2 text-xs text-gray-400 lg:mt-5">
                      <p className="text-center">
                        By signing up, you agree to ifèbaɪk <br />{" "}
                        <span className="font-medium text-black hover:underline">
                          Terms & Conditions
                        </span>{" "}
                        and{" "}
                        <span className="font-medium text-black hover:underline">
                          Privacy policy
                        </span>
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {previewType === "confirm" && (
                <motion.div
                  key="confirm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex h-full w-full items-center"
                >
                  <div className="w-full rounded-3xl py-10 xl:p-10 xl:shadow-2xl">
                    <h2 className="font-archivo mb-2 text-[2.4rem] leading-[1] font-medium text-black/85">
                      Check your email
                    </h2>

                    <p className="text-sm font-normal text-gray-500">
                      We have sent an email to{" "}
                      <span className="text-base font-medium text-gray-700">
                        {userEmail}!
                      </span>{" "}
                      If this email is associated with billzpay account, you'll
                      be able to reset your password. If you received this
                      email, please click to validate your code.
                    </p>

                    <div className="mt-8 flex w-full flex-col gap-4">
                      <button
                        onClick={() => setPreviewType("codeConfirm")}
                        className="btn w-full cursor-pointer rounded px-8 py-2.5 font-medium text-white transition-all duration-150"
                      >
                        Validate Code
                      </button>

                      <button
                        onClick={() => setPreviewType("reset")}
                        className="w-full rounded border border-black px-8 py-2.5 font-medium text-black transition-all duration-150 hover:bg-black/5"
                      >
                        Resend Email
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {previewType === "codeConfirm" && (
                <motion.div
                  key="codeConfirm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex h-full w-full items-center"
                >
                  <div className="w-full rounded-3xl py-10 xl:p-10 xl:shadow-2xl">
                    <h2 className="font-archivo mb-2 text-[2.3rem] leading-[1] font-medium text-black/85">
                      Reset code verification
                    </h2>

                    <p className="leading-[1.3] text-gray-500">
                      To recover your password, please enter the password reset
                      code we sent to your email.
                    </p>

                    <div className="mt-4 flex w-full flex-col gap-4">
                      <InputOTP
                        maxLength={6}
                        value={otpValue}
                        onChange={handleOTPChange}
                        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot
                            index={0}
                            className="h-12 w-14 text-lg font-medium"
                          />
                          <InputOTPSlot
                            index={1}
                            className="h-12 w-14 text-lg font-medium"
                          />
                          <InputOTPSlot
                            index={2}
                            className="h-12 w-14 text-lg font-medium"
                          />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot
                            index={3}
                            className="h-12 w-14 text-lg font-medium"
                          />
                          <InputOTPSlot
                            index={4}
                            className="h-12 w-14 text-lg font-medium"
                          />
                          <InputOTPSlot
                            index={5}
                            className="h-12 w-14 text-lg font-medium"
                          />
                        </InputOTPGroup>
                      </InputOTP>

                      <div>
                        <button
                          onClick={handleOTPSubmit}
                          disabled={otpValue.length !== 6 || isOTPLoading}
                          className={`btn cursor-pointer rounded-md px-6 py-3 text-sm font-medium text-white disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 ${isOTPLoading && "bg-black/60"}`}
                        >
                          {isOTPLoading ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="animate-spin" /> Validating
                              code...
                            </div>
                          ) : (
                            "Verify Code"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <AuthDisplay />
        </div>
      </section>
    </>
  );
};

export default ResetPassword;
